/**
 * Mock API Client Adapter
 * Wraps existing mockApi to implement ApiClient interface
 */

import { delay } from '@/lib/utils';
import { mockApi } from '@/lib/mockApi';
import type { ApiClient, CreateProductInput, UpdateProductInput, CreateOrderInput, AddCartItemInput, HomepageConfigDTO } from '../client';
import type { Product, Order, OrderStatus, Cart, User, Shop, Category } from '../types';

const mockClient: ApiClient = {
  auth: {
    async register(input: { email: string; password: string }): Promise<{ ok: true }> {
      // In mock mode, create user and mark as verified immediately for smooth dev
      await delay(300);
      const user = await mockApi.login(input.email, '123456');
      // Mark as verified in mock storage (if mockApi supports it)
      // For now, just return success - mock users are auto-verified
      return { ok: true as const };
    },
    async login(input: { email: string; password: string }): Promise<{ user: User }> {
      // Mock mode uses OTP, so we'll use password as OTP for compatibility
      const user = await mockApi.login(input.email, input.password || '123456');
      return { user };
    },
    async logout(): Promise<{ ok: true }> {
      await mockApi.logout();
      return { ok: true };
    },
    async me(): Promise<{ user: User }> {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      return { user };
    },
    async forgotPassword(email: string): Promise<{ ok: true; token?: string }> {
      const token = await mockApi.requestPasswordReset(email);
      return { ok: true, token };
    },
    async resetPassword(input: { token: string; newPassword: string }): Promise<{ ok: true }> {
      await mockApi.resetPassword(input.token, input.newPassword);
      return { ok: true };
    },
    async changePassword(input: { currentPassword: string; newPassword: string }): Promise<{ ok: true }> {
      await mockApi.changePassword(input.currentPassword, input.newPassword);
      return { ok: true };
    },
    async verifyEmail(token: string): Promise<{ ok: true }> {
      // In mock mode, always succeed (users are auto-verified)
      await delay(300);
      return { ok: true as const };
    },
    async resendVerification(email: string): Promise<{ ok: true }> {
      // In mock mode, always succeed
      await delay(300);
      return { ok: true as const };
    },
  },
  products: {
    async getProducts(params) {
      const allProducts = await mockApi.getProducts(params);
      const limit = params?.limit ?? 20;
      const offset = params?.offset ?? 0;
      const items = allProducts.slice(offset, offset + limit);
      return {
        items,
        total: allProducts.length,
        limit,
        offset,
      };
    },

    async getProduct(id) {
      return mockApi.getProduct(id);
    },

    async createProduct(input) {
      // mockApi.createProduct expects Omit<Product, 'id'>
      return mockApi.createProduct(input);
    },

    async updateProduct(id, patch) {
      return mockApi.updateProduct(id, patch);
    },
  },
  orders: {
    async getOrders(params) {
      const allOrders = await mockApi.getOrders(params);
      const limit = params?.limit ?? 20;
      const offset = params?.offset ?? 0;
      const items = allOrders.slice(offset, offset + limit);
      return {
        items,
        total: allOrders.length,
        limit,
        offset,
      };
    },

    async getOrder(id) {
      return mockApi.getOrder(id);
    },

    async createOrder(input) {
      // mockApi.createOrder expects (userId: string, address: Order['address'])
      return mockApi.createOrder(input.userId, input.address);
    },

    async updateOrderStatus(id, status, byRole) {
      return mockApi.updateOrderStatus(id, status, byRole);
    },
  },
  cart: {
    async getCart() {
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      return mockApi.getCart(user.id);
    },

    async addItem(input) {
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      // mockApi.addToCart expects (userId: string, productId: string, qty: number)
      const qty = input.quantity ?? 1;
      return mockApi.addToCart(user.id, input.productId, qty);
    },

    async updateItemQty(productId, quantity) {
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      // mockApi.updateCartItem expects (userId: string, productId: string, qty: number)
      return mockApi.updateCartItem(user.id, productId, quantity);
    },

    async removeItem(productId) {
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      return mockApi.removeFromCart(user.id, productId);
    },

    async clearCart() {
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      return mockApi.clearCart(user.id);
    },
  },
  admin: {
    async listUsers() {
      // Mock implementation - return demo users
      const demoUsers = [
        { id: 'user1', email: 'user@nomadshop.mn', role: 'user' as const, createdAt: new Date().toISOString() },
        { id: 'seller1', email: 'seller@nomadshop.mn', role: 'seller' as const, createdAt: new Date().toISOString() },
        { id: 'admin1', email: 'admin@nomadshop.mn', role: 'admin' as const, createdAt: new Date().toISOString() },
      ];
      return { users: demoUsers };
    },
    async updateUserRole(userId: string, role: 'user' | 'seller' | 'admin') {
      // Mock implementation - just return updated user
      await delay(300);
      return {
        user: {
          id: userId,
          email: `${role}@nomadshop.mn`,
          role,
          createdAt: new Date().toISOString(),
        },
      };
    },
    async getHomepageConfig() {
      // Load from localStorage or return default
      const { getItem } = await import('@/lib/safeStorage');
      const stored = getItem('nomadshop_homepage_config');
      if (stored) {
        try {
          return { config: JSON.parse(stored) as HomepageConfigDTO };
        } catch {
          // Invalid JSON, return default
        }
      }
      
      const defaultConfig: HomepageConfigDTO = {
        banners: [
          {
            title: 'Welcome to NomadShop',
            subtitle: 'Discover amazing products',
            imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200',
            href: '/',
          },
        ],
        featuredCategoryIds: [],
        featuredProductIds: [],
      };
      
      return { config: defaultConfig };
    },
    async updateHomepageConfig(input: HomepageConfigDTO) {
      // Save to localStorage
      const { setItem } = await import('@/lib/safeStorage');
      setItem('nomadshop_homepage_config', JSON.stringify(input));
      await delay(300);
      return { config: input };
    },
    async generateDemoData(input?: { productsPerShop?: number; ordersPerShop?: number }) {
      // Mock implementation - just return success
      // In mock mode, data is already generated via mockApi
      await delay(500);
      return {
        ok: true as const,
        created: {
          shops: 1,
          products: input?.productsPerShop || 20,
          orders: input?.ordersPerShop || 10,
        },
      };
    },
  },
  seller: {
    async getMyShop() {
      const user = mockApi.getCurrentUser();
      if (!user) {
        return { shop: null };
      }
      const shop = await mockApi.getMyShop(user.id);
      return { shop };
    },
    async createShop(input: { name: string; contactEmail?: string; contactPhone?: string; shippingPolicy?: string; logoUrl?: string; bannerUrl?: string }) {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      // Check if shop already exists
      const existing = await mockApi.getMyShop(user.id);
      if (existing) {
        throw new Error('Shop already exists');
      }
      // Create shop in mockApi
      const shops = await mockApi.getShops();
      const newShop: Shop = {
        id: `shop_${Date.now()}`,
        ownerUserId: user.id,
        name: input.name,
        contactEmail: input.contactEmail,
        contactPhone: input.contactPhone,
        shippingPolicy: input.shippingPolicy,
        logoUrl: input.logoUrl,
        bannerUrl: input.bannerUrl,
      };
      shops.push(newShop);
      // Save to localStorage
      const { setItem } = await import('@/lib/safeStorage');
      setItem('nomadshop_shops', JSON.stringify(shops));
      await delay(300);
      return { shop: newShop };
    },
    async updateMyShop(patch: Partial<{ name: string; contactEmail?: string; contactPhone?: string; shippingPolicy?: string; logoUrl?: string; bannerUrl?: string }>) {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      const shop = await mockApi.getMyShop(user.id);
      if (!shop) {
        throw new Error('Shop not found');
      }
      const updated = await mockApi.updateShop(shop.id, patch);
      return { shop: updated };
    },
    async getOrders() {
      await delay(300);
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      const shops = await mockApi.getShops();
      const shop = shops.find(s => s.ownerUserId === user.id);
      if (!shop) return [];
      const orders = await mockApi.getOrders({ shopId: shop.id });
      return orders;
    },
    async updateOrderStatus(orderId: string, status: OrderStatus) {
      await delay(500);
      const user = mockApi.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      const shops = await mockApi.getShops();
      const shop = shops.find(s => s.ownerUserId === user.id);
      if (!shop) throw new Error('Shop not found');
      const order = await mockApi.getOrder(orderId);
      if (!order || order.shopId !== shop.id) {
        throw new Error('Order not found or access denied');
      }
      const updated = await mockApi.updateOrderStatus(orderId, status, 'seller');
      return updated;
    },
  },
  wishlist: {
    async getWishlist() {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      const productIds = await mockApi.getWishlist(user.id);
      // Resolve products
      const products = await Promise.all(
        productIds.map(id => mockApi.getProduct(id))
      );
      return { products: products.filter((p): p is Product => p !== null) };
    },
    async addToWishlist(productId: string) {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      await mockApi.addToWishlist(user.id, productId);
      // Return updated wishlist
      return this.getWishlist();
    },
    async removeFromWishlist(productId: string) {
      const user = mockApi.getCurrentUser();
      if (!user) {
        throw new Error('Not authenticated');
      }
      await mockApi.removeFromWishlist(user.id, productId);
      // Return updated wishlist
      return this.getWishlist();
    },
  },
  categories: {
    async getCategories(params?: { parentId?: string }) {
      const categories = await mockApi.getCategories();
      let filtered = categories;
      if (params?.parentId) {
        filtered = categories.filter(c => c.parentId === params.parentId);
      }
      return { items: filtered };
    },
    async getCategory(id: string) {
      const categories = await mockApi.getCategories();
      const category = categories.find(c => c.id === id);
      if (!category) {
        throw new Error('Category not found');
      }
      return { item: category };
    },
  },
  payments: {
    async createQPayPayment(orderId: string) {
      // Mock: return fake payment data
      await delay(500);
      return {
        paymentId: `payment_${Date.now()}`,
        qrTextOrUrl: 'https://api.qpay.mn/qr/test-qr-code',
        qrImageBase64: undefined, // Mock doesn't provide image
        providerInvoiceId: `INV-${orderId}`,
      };
    },
  },
  homepage: {
    async getPublic() {
      // Return default config with demo banners
      const defaultConfig: HomepageConfigDTO = {
        banners: [
          {
            title: 'Welcome to NomadShop',
            subtitle: 'Discover amazing products',
            imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200',
            href: '/',
          },
        ],
        featuredCategoryIds: [],
        featuredProductIds: [],
      };
      
      // Try to load from localStorage if exists
      const { getItem } = await import('@/lib/safeStorage');
      const stored = getItem('nomadshop_homepage_config');
      if (stored) {
        try {
          return { config: JSON.parse(stored) as HomepageConfigDTO };
        } catch {
          // Invalid JSON, return default
        }
      }
      
      return { config: defaultConfig };
    },
  },
};

export default mockClient;

